import { useMemo, useState } from 'react';
import { useParams } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  BookOpen,
  ChevronDown,
  ChevronRight,
  FileText,
  History,
  Plus,
  Save,
  Search,
  Trash2,
} from 'lucide-react';

import type { WikiPage, WikiPageSummary, WikiTemplate } from '@tasks/contracts';

import {
  createPage,
  deletePage,
  getPage,
  listPages,
  listTemplates,
  searchPages,
  updatePage,
} from '../../api/wiki';
import { queryKeys } from '../../api/queryKeys';
import { Button } from '../../components/Button';
import { ConfirmDialog } from '../../components/ConfirmDialog';
import { EmptyState } from '../../components/EmptyState';
import { IconButton } from '../../components/IconButton';
import { Input } from '../../components/Input';
import { MarkdownEditor } from '../../components/MarkdownEditor';
import { MarkdownView } from '../../components/MarkdownView';
import { Modal } from '../../components/Modal';
import { Skeleton } from '../../components/Skeleton';
import { useToast } from '../../components/Toast';
import { humanizeError } from '../../lib/errors';
import { WikiHistory } from './WikiHistory';
import { cn } from '../../lib/cn';
import styles from './WikiPage.module.css';

/**
 * Wiki проекта (docs/14 §10).
 *
 * Двухколоночный вид: слева дерево страниц, справа — просмотр и редактор.
 * Wiki независима от git: всё живёт в БД, а обмен с внешним миром — в
 * настройках проекта (импорт/экспорт архива).
 */
export function WikiPageView() {
  const { key = '', pageId } = useParams<{ key: string; pageId?: string }>();
  const queryClient = useQueryClient();
  const toast = useToast();

  const [search, setSearch] = useState('');
  const [isEditing, setIsEditing] = useState(false);
  const [isHistoryOpen, setHistoryOpen] = useState(false);
  const [draft, setDraft] = useState<{ title: string; body: string } | null>(null);
  const [pageToDelete, setPageToDelete] = useState<WikiPageSummary | null>(null);
  const [isTemplateOpen, setTemplateOpen] = useState(false);

  const pagesQuery = useQuery({
    queryKey: queryKeys.projects.wikiPages(key),
    queryFn: ({ signal }) => listPages(key, signal),
    enabled: Boolean(key),
  });

  const pages = pagesQuery.data ?? [];
  const selectedId = pageId ?? pages[0]?.id ?? null;

  const pageQuery = useQuery({
    queryKey: queryKeys.projects.wikiPage(key, selectedId ?? ''),
    queryFn: ({ signal }) => getPage(key, selectedId as string, signal),
    enabled: Boolean(key) && selectedId !== null,
  });

  const page = pageQuery.data ?? null;

  const templatesQuery = useQuery({
    queryKey: ['projects', key, 'wiki', 'templates'],
    queryFn: ({ signal }) => listTemplates(key, signal),
    enabled: isTemplateOpen,
  });

  const searchQuery = useQuery({
    queryKey: queryKeys.projects.wikiSearch(key, search),
    queryFn: ({ signal }) => searchPages(key, search, signal),
    enabled: search.trim().length >= 2,
  });

  const invalidateAll = async (): Promise<void> => {
    await queryClient.invalidateQueries({ queryKey: queryKeys.projects.wikiPages(key) });
    if (selectedId) {
      await queryClient.invalidateQueries({
        queryKey: queryKeys.projects.wikiPage(key, selectedId),
      });
      await queryClient.invalidateQueries({
        queryKey: queryKeys.projects.wikiVersions(key, selectedId),
      });
    }
  };

  const createMutation = useMutation({
    mutationFn: (parentId: string | null) =>
      createPage(key, {
        title: 'Новая страница',
        body: '',
        parentId,
        status: 'draft',
        labels: [],
      }),
    onSuccess: async (created) => {
      await invalidateAll();
      setDraft({ title: created.title, body: created.body });
      setIsEditing(true);
    },
    onError: (error: unknown) => toast.error(humanizeError(error).message),
  });

  const createFromTemplateMutation = useMutation({
    mutationFn: (template: WikiTemplate) =>
      createPage(key, {
        title: template.name,
        body: template.body,
        parentId: null,
        status: 'draft',
        labels: [],
      }),
    onSuccess: async (created) => {
      setTemplateOpen(false);
      await invalidateAll();
      setDraft({ title: created.title, body: created.body });
      setIsEditing(true);
    },
    onError: (error: unknown) => toast.error(humanizeError(error).message),
  });

  const saveMutation = useMutation({
    mutationFn: (input: { title: string; body: string }) =>
      updatePage(key, selectedId as string, {
        title: input.title,
        body: input.body,
        version: page?.version,
      }),
    onSuccess: async () => {
      await invalidateAll();
      setIsEditing(false);
      toast.success('Страница сохранена');
    },
    onError: (error: unknown) => toast.error(humanizeError(error).message),
  });

  const deleteMutation = useMutation({
    mutationFn: (target: WikiPageSummary) => deletePage(key, target.id),
    onSuccess: async () => {
      await invalidateAll();
      setPageToDelete(null);
      toast.success('Страница удалена');
    },
    onError: (error: unknown) => toast.error(humanizeError(error).message),
  });

  const tree = useMemo(() => buildTree(pages), [pages]);

  if (pagesQuery.isLoading) {
    return <Skeleton height="400px" />;
  }

  return (
    <div className={styles['layout']}>
      <aside className={styles['tree']} aria-label="Страницы вики">
        <div className={styles['treeHead']}>
          <Input
            value={search}
            onChange={(event) => setSearch(event.target.value)}
            placeholder="Поиск по вики"
            aria-label="Поиск по вики"
          />
        </div>

        <Button
          variant="secondary"
          icon={<Plus size={16} />}
          onClick={() => createMutation.mutate(null)}
          loading={createMutation.isPending}
        >
          Создать страницу
        </Button>
        <Button variant="ghost" icon={<FileText size={16} />} onClick={() => setTemplateOpen(true)}>
          Из шаблона
        </Button>

        {search.trim().length >= 2 ? (
          <ul className={styles['treeList']}>
            {(searchQuery.data ?? []).map((item) => (
              <li key={item.id}>
                <a className={styles['treeLink']} href={`/projects/${key}/wiki/${item.id}`}>
                  <Search size={14} aria-hidden />
                  <span>{item.title}</span>
                </a>
              </li>
            ))}
            {searchQuery.data?.length === 0 ? (
              <li className={styles['treeEmpty']}>Ничего не найдено</li>
            ) : null}
          </ul>
        ) : (
          <WikiTree nodes={tree} selectedId={selectedId} projectKey={key} />
        )}
      </aside>

      <section className={styles['content']}>
        {selectedId === null ? (
          <EmptyState
            icon={<BookOpen size={24} />}
            title="В вики пока нет страниц"
            description="Создайте первую страницу: она станет базой знаний проекта."
            action={
              <Button onClick={() => createMutation.mutate(null)} icon={<Plus size={16} />}>
                Создать страницу
              </Button>
            }
          />
        ) : pageQuery.isLoading || page === null ? (
          <Skeleton height="320px" />
        ) : (
          <WikiPageBody
            page={page}
            projectKey={key}
            isEditing={isEditing}
            draft={draft ?? { title: page.title, body: page.body }}
            onDraftChange={setDraft}
            onEdit={() => {
              setDraft({ title: page.title, body: page.body });
              setIsEditing(true);
            }}
            onCancel={() => setIsEditing(false)}
            onSave={() => saveMutation.mutate(draft ?? { title: page.title, body: page.body })}
            isSaving={saveMutation.isPending}
            onHistory={() => setHistoryOpen(true)}
            onDelete={() => setPageToDelete(page)}
            onChildCreate={() => createMutation.mutate(page.id)}
          />
        )}
      </section>

      {isHistoryOpen && page ? (
        <WikiHistory
          projectKey={key}
          pageId={page.id}
          onClose={() => setHistoryOpen(false)}
          onReverted={() => {
            void invalidateAll();
            setHistoryOpen(false);
          }}
        />
      ) : null}

      <Modal
        open={isTemplateOpen}
        onClose={() => setTemplateOpen(false)}
        title="Создать страницу из шаблона"
      >
        {templatesQuery.isLoading ? (
          <Skeleton height="120px" />
        ) : (
          <ul className={styles['templateList']}>
            {(templatesQuery.data ?? []).map((template) => (
              <li key={template.id}>
                <button
                  type="button"
                  className={styles['templatePick']}
                  disabled={createFromTemplateMutation.isPending}
                  onClick={() => createFromTemplateMutation.mutate(template)}
                >
                  <strong>{template.name}</strong>
                  <span className={styles['templatePreview']}>
                    {template.body.split('\n').slice(0, 2).join(' ')}
                  </span>
                </button>
              </li>
            ))}
          </ul>
        )}
      </Modal>

      <ConfirmDialog
        open={pageToDelete !== null}
        title="Удалить страницу?"
        description={
          pageToDelete
            ? `«${pageToDelete.title}» будет удалена. История версий сохранится.`
            : undefined
        }
        confirmLabel="Удалить"
        danger
        onClose={() => setPageToDelete(null)}
        onConfirm={() => pageToDelete && deleteMutation.mutate(pageToDelete)}
      />
    </div>
  );
}

interface WikiTreeProps {
  readonly nodes: TreeNode[];
  readonly selectedId: string | null;
  readonly projectKey: string;
  readonly depth?: number;
}

function WikiTree({ nodes, selectedId, projectKey, depth = 0 }: WikiTreeProps) {
  return (
    <ul className={styles['treeList']} style={{ paddingLeft: depth === 0 ? 0 : 12 }}>
      {nodes.map((node) => (
        <TreeItem
          key={node.page.id}
          node={node}
          selectedId={selectedId}
          projectKey={projectKey}
          depth={depth}
        />
      ))}
    </ul>
  );
}

function TreeItem({
  node,
  selectedId,
  projectKey,
  depth,
}: {
  readonly node: TreeNode;
  readonly selectedId: string | null;
  readonly projectKey: string;
  readonly depth: number;
}) {
  const [isOpen, setOpen] = useState(true);
  const hasChildren = node.children.length > 0;

  return (
    <li>
      <div
        className={cn(
          styles['treeRow'],
          node.page.id === selectedId && styles['treeRowActive'],
        )}
      >
        {hasChildren ? (
          <IconButton
            label={isOpen ? 'Свернуть' : 'Развернуть'}
            size="sm"
            icon={isOpen ? <ChevronDown size={14} /> : <ChevronRight size={14} />}
            onClick={() => setOpen((value) => !value)}
          />
        ) : (
          <span className={styles['treeSpacer']} aria-hidden />
        )}
        <a className={styles['treeLink']} href={`/projects/${projectKey}/wiki/${node.page.id}`}>
          <FileText size={14} aria-hidden />
          <span>{node.page.title}</span>
          {node.page.status === 'draft' ? (
            <span className={styles['draftBadge']}>черновик</span>
          ) : null}
        </a>
      </div>
      {hasChildren && isOpen ? (
        <WikiTree
          nodes={node.children}
          selectedId={selectedId}
          projectKey={projectKey}
          depth={depth + 1}
        />
      ) : null}
    </li>
  );
}

interface WikiPageBodyProps {
  readonly page: WikiPage;
  readonly projectKey: string;
  readonly isEditing: boolean;
  readonly draft: { title: string; body: string };
  readonly onDraftChange: (draft: { title: string; body: string }) => void;
  readonly onEdit: () => void;
  readonly onCancel: () => void;
  readonly onSave: () => void;
  readonly isSaving: boolean;
  readonly onHistory: () => void;
  readonly onDelete: () => void;
  readonly onChildCreate: () => void;
}

function WikiPageBody({
  page,
  isEditing,
  draft,
  onDraftChange,
  onEdit,
  onCancel,
  onSave,
  isSaving,
  onHistory,
  onDelete,
  onChildCreate,
}: WikiPageBodyProps) {
  return (
    <article className={styles['page']}>
      <header className={styles['pageHead']}>
        {isEditing ? (
          <Input
            value={draft.title}
            onChange={(event) => onDraftChange({ ...draft, title: event.target.value })}
            aria-label="Заголовок страницы"
          />
        ) : (
          <h1 className={styles['title']}>{page.title}</h1>
        )}

        <div className={styles['actions']}>
          {isEditing ? (
            <>
              <Button variant="secondary" onClick={onCancel}>
                Отмена
              </Button>
              <Button onClick={onSave} loading={isSaving} icon={<Save size={16} />}>
                Сохранить
              </Button>
            </>
          ) : (
            <>
              <Button variant="secondary" onClick={onChildCreate} icon={<Plus size={16} />}>
                Дочерняя
              </Button>
              <Button variant="secondary" onClick={onHistory} icon={<History size={16} />}>
                История
              </Button>
              <Button onClick={onEdit}>Редактировать</Button>
              <IconButton
                label="Удалить страницу"
                tone="danger"
                icon={<Trash2 size={16} />}
                onClick={onDelete}
              />
            </>
          )}
        </div>
      </header>

      {page.labels.length > 0 ? (
        <ul className={styles['labels']}>
          {page.labels.map((label) => (
            <li key={label} className={styles['label']}>
              {label}
            </li>
          ))}
        </ul>
      ) : null}

      {isEditing ? (
        <MarkdownEditor
          value={draft.body}
          onChange={(body) => onDraftChange({ ...draft, body })}
          placeholder="Текст страницы в markdown"
          minRows={16}
        />
      ) : page.body.trim().length === 0 ? (
        <p className={styles['emptyBody']}>Страница пока пуста. Нажмите «Редактировать».</p>
      ) : (
        <MarkdownView value={page.body} />
      )}

      <footer className={styles['pageFoot']}>
        <span>
          Версия {page.version}
          {page.updatedBy ? ` · изменил ${page.updatedBy.displayName}` : ''}
        </span>
      </footer>
    </article>
  );
}

interface TreeNode {
  readonly page: WikiPageSummary;
  readonly children: TreeNode[];
}

/** Дерево из плоского списка: порядок — по rank, как его отдал сервер. */
function buildTree(pages: readonly WikiPageSummary[]): TreeNode[] {
  const byParent = new Map<string | null, WikiPageSummary[]>();

  for (const page of pages) {
    const list = byParent.get(page.parentId) ?? [];
    list.push(page);
    byParent.set(page.parentId, list);
  }

  const build = (parentId: string | null): TreeNode[] =>
    (byParent.get(parentId) ?? []).map((page) => ({
      page,
      children: build(page.id),
    }));

  return build(null);
}
